import { ComponentStyleConfig } from "@chakra-ui/react";
import { SystemStyleObject } from "@chakra-ui/styled-system";
import { theme as chakraTheme } from "@chakra-ui/theme";

const stylesForColorScheme: Record<string, SystemStyleObject> = {
  primary: {
    bg: "white",
    borderColor: "gray.200",
  },
  success: {
    bg: "white",
    borderColor: "primary.600",
    color: "primary.600",
  },
  error: {
    bg: "error.25",
    borderColor: "error.300",
    color: "error.700",
  },
};

const CardTheme: ComponentStyleConfig = {
  baseStyle: ({ colorScheme }) => ({
    container: {
      ...stylesForColorScheme[colorScheme],
      borderWidth: 1,
      boxShadow: "sm",
      overflow: "hidden",
      flexShrink: 0,
      _focusVisible: {
        outlineColor: "primary.100",
      },
    },
    body: {
      pb: 0,
      pt: 0,
    },
    footer: {
      alignItems: "flex-end",
      display: "flex",
      justifyContent: "space-between",
      pt: 0,
      pb: 0,
      px: "24px",
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      py: 0,
      ".chakra-text": {
        color: "inherit",
      },
    },
  }),
  variants: {
    stat: {
      body: {
        pt: 0,
        pb: 0,
        mt: 0,
        m: 0,
        gap: "32px",
        whiteSpace: "nowrap",
      },
      container: {
        borderColor: "gray.200",
        borderWidth: 1,
        py: 0,
      },
    },
    receipt: {
      container: {
        padding: "24px",
      },
      body: {
        display: "flex",
        flexDirection: "column",
        gap: "40px",
        "&&": {
          p: 0,
          ".chakra-stat__group": {
            gap: "16px",
            justifyContent: "flex-start",
            py: 0,
          },
          ".chakra-stat dl": {
            gap: 0,
          },
        },
      },
    },
    descriptions: {
      container: {
        py: 0,
        gap: 0,
      },
      header: {
        pl: "40px",
        pr: "16px",
        minHeight: "64px",
      },
      body: {
        maxWidth: "744px",
        mx: "64px",
        px: 0,
        pt: 0,
        pb: "49px",
      },
    },
    form: {
      container: {
        gap: 0,
      },
      body: {
        mt: "12px",
        mx: "64px",
        maxWidth: "560px",
        px: 0,
      },
    },
    table: {
      container: {
        gap: "0",
        py: "0",
      },
      header: {
        py: "8px",
        p: {
          textStyle: "h6",
          fontWeight: "medium",
        },
      },
      body: {
        borderTop: "1px solid",
        borderColor: "gray.200",
        py: "12px",
      },
    },
    clickable: {
      container: {
        borderRadius: "12px",
        borderWidth: "0",
        boxShadow:
          "0px 12px 16px -4px rgba(16, 24, 40, 0.05), 0px 4px 6px -2px rgba(16, 24, 40, 0.01)",
        py: "12px",
        _hover: {
          bg: "teal.25",
          cursor: "pointer",
        },
        _active: {
          bg: "teal.50",
        },
      },
      body: {
        a: {
          _hover: {
            textDecoration: "none",
          },
        },
      },
    },
    chart: {
      container: {
        p: 0,
        border: 0,
        borderRadius: "12px",
        boxShadow: "lg",
      },
      header: {
        textStyle: "overline",
        // paddingInline: 0,
        ml: 0,
        mt: "24px",
        w: "100%",
        justifyContent: "center",
        alignItems: "center",
        color: "gray.500",
      },
      body: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        paddingInline: 0,
        ".mb-card-section-title": {
          ml: 0,
          mt: "24px",
          w: "100%",
          justifyContent: "center",
          alignItems: "center",
          color: "gray.500",
        },
        ".mb-card-section-body": {
          p: "24px",
        },
        ".mb-card-section-group": {
          gap: 0,
          ".chakra-stack__divider": {
            borderLeftWidth: "2px",
            borderLeftColor: "gray.100",
          },
        },
      },
    },
  },
  sizes: {
    md: {
      body: {
        paddingInline: "24px",
        "& .mb-card-section-group": {
          gap: "32px",
          minWidth: 0,
        },
        "& .mb-card-section": {
          _first: {
            ml: 0,
          },
          _last: {
            mr: 0,
          },
        },
        "& .chakra-stat__group": {
          py: "16px",
          gap: "32px",
          flexGrow: 1,
        },
      },
      header: {
        px: "24px",
      },
      container: {
        borderRadius: "8px",
        py: "24px",
        gap: "24px",
      },
    },
    sm: {
      container: {
        py: "16px",
        gap: "16px",
      },
      header: {
        paddingStart: "16px",
        paddingEnd: "16px",
      },
      body: {
        paddingInline: "16px",
      },
    },
    xs: {
      body: {
        px: 0,
        display: "flex",
        minHeight: "40px",
        alignItems: "center",
        "& .chakra-stat__group": {
          alignItems: "center",
          p: 0,
          px: "16px",
          py: "12px",
          gap: "24px",
        },
      },
      container: {
        borderRadius: "4px",
      },
    },
  },
  parts: chakraTheme.components.Card.parts,
};

export default CardTheme;
